/**
 * URL encoding for a design brief, so an options page is shareable too.
 *
 * Designs get the compact RLE encoding in engine/serialize.ts because they
 * contain a whole knot grid. A brief is a handful of short strings, so plain
 * JSON in base64url is small enough and much easier to keep forward-compatible.
 */
import { fromBase64Url, toBase64Url } from '../engine/serialize';
import { DEFAULT_SIZING } from './types';
import type { DesignBrief } from './types';
import { flossByCode } from '../data/dmc';

export function encodeBrief(brief: DesignBrief): string {
  return toBase64Url(
    JSON.stringify({
      t: brief.text,
      c: brief.colors,
      s: brief.stylePreference,
      w: brief.word ?? '',
      m: brief.motifs,
      d: brief.mood,
      z: [brief.sizing.wristCm, brief.sizing.easeCm, brief.sizing.closure],
      o: brief.source,
      n: brief.sourceNote,
    }),
  );
}

export function decodeBrief(encoded: string): DesignBrief | null {
  try {
    const raw = JSON.parse(fromBase64Url(encoded));
    const colors = Array.isArray(raw.c) ? raw.c.filter((c: unknown) => typeof c === 'string' && flossByCode(c)) : [];
    if (colors.length < 2) return null;
    return {
      text: typeof raw.t === 'string' ? raw.t : '',
      colors,
      stylePreference: ['normal', 'alpha', 'chain', 'any'].includes(raw.s) ? raw.s : 'any',
      word: typeof raw.w === 'string' && raw.w ? raw.w : undefined,
      motifs: Array.isArray(raw.m) ? raw.m : [],
      mood: typeof raw.d === 'string' ? raw.d : '',
      sizing: Array.isArray(raw.z)
        ? {
            wristCm: Number(raw.z[0]) || DEFAULT_SIZING.wristCm,
            easeCm: Number.isFinite(Number(raw.z[1])) ? Number(raw.z[1]) : DEFAULT_SIZING.easeCm,
            closure: raw.z[2] === 'loop-and-tie' ? 'loop-and-tie' : 'tie',
          }
        : DEFAULT_SIZING,
      source: ['heuristic', 'gemini', 'photo', 'manual'].includes(raw.o) ? raw.o : 'heuristic',
      sourceNote: typeof raw.n === 'string' ? raw.n : '',
    };
  } catch {
    return null;
  }
}
